<script>
import { CustomService } from '@/services/api/custom.service';
import { mapGetters, mapMutations, mapState } from 'vuex';
import RexModalV3 from '@/rex-components/RexModalV3.vue';
import RexButtonV3 from '@/rex-components/RexButtonV3.vue';
import LogWidgetAddDashboardPage from '@/components/Log/stepper-pages/LogWidgetAddDashboardPage.vue';
import { NAME_FIELD_REGEX } from '@/components/Log/log.constants';
import {
  getMultiChartGraphConfig,
  getGraphConfig,
} from '@/utils/custom/custom.log.widgets.config';
import { notificationError, notificationSucess } from '@/utils/notification';
import { addToDashboardRadioGroupOptions } from '@/components/Cloud/Dashboard/cloud.types';

export default {
  components: {
    RexModalV3,
    RexButtonV3,
    LogWidgetAddDashboardPage,
  },
  props: {
    showAddToDashboard: {
      type: Boolean,
      default: false,
    },
    instances: {
      type: Array,
      default: () => [],
    },
    chartConfig: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['onClose'],
  mounted() {
    this.setAddDashboardInfoFields({
      field: 'widgetName',
      value: this.chartConfig.isMultiMetric
        ? this.chartConfig.title
        : this.chartConfig.header.title,
    });
  },
  computed: {
    ...mapGetters('logState/logCommonState', [
      'getWidgetAddDashboardInfo',
      'getWidgetFormData',
    ]),
    ...mapState('cloudState/detail', ['currentServiceInstance']),
    getIsNewDash() {
      return this.getWidgetFormData.isNewDash;
    },
    getWidgetNameValid() {
      return this.getWidgetFormData.isWidgetNameValid;
    },
    getDashNameValid() {
      return this.getWidgetFormData.isDashNameValid;
    },
    getDashboardValid() {
      return this.getWidgetFormData.isDashboardValid;
    },
    getMultiChartGraphConfig() {
      return getMultiChartGraphConfig({
        width: '48%',
        title: this.getWidgetAddDashboardInfo.widgetName,
        header: {
          title: '',
          titleIcon: '',
          content: JSON.stringify(this.chartConfig.headers),
        },
        formula: this.chartConfig.formulaName ?? 'roundUpToThreeDecimal',
        serviceKey: this.$route.params.serviceId,
        capability: 'C',
        chartColor: [],
        height: '12.5em',
        valueColumn: this.chartConfig.valueColumn,
        graphTitles: this.chartConfig.graphTitles,
        entityType: this.chartConfig.entityType,
        instances: this.instances,
        metricType: this.chartConfig.metricType || 'REGULAR_VALUE',
        metricName: this.chartConfig.metricName,
        queryMetricType: this.chartConfig.queryMetricType,
        chartType: 'MultiLineGraph',
        unit: 'CPM - calls per minute',
        axisLabels: {
          x: 'Time',
          y: 'Count',
        },
        labels: this.chartConfig.graphTitles,
        widgetName: this.getWidgetAddDashboardInfo.widgetName,
        actionPath: 'cloudState/detail',
        aggregationInfo: this.chartConfig.aggregationInfo,
      });
    },
    getGraphConfig() {
      return getGraphConfig({
        widgetName: this.getWidgetAddDashboardInfo.widgetName,
        serviceKey: this.$route.params.serviceId,
        xLabel: 'Time',
        yLabel: this.chartConfig.axisLabels.y,
        savedSearchName: null,
        chartType: this.chartConfig.chartType,
        capability: 'C',
        queryMetric: this.chartConfig.metricName,
        instanceIds: this.instances,
        aggregationInfo: this.chartConfig.aggregationInfo,
      });
    },
  },
  data() {
    return {
      radioGroupOptions: addToDashboardRadioGroupOptions,
      widgetName: '',
      dashboardName: '',
      selectedTemplate: '',
      isWidgetCreated: false,
      isNewDashCreated: false,
      dashboardId: '',
    };
  },
  methods: {
    ...mapMutations('logState/logCommonState', [
      'setAddDashboardInfoFields',
      'setWidgetInfoFields',
      'setWidgetFormData',
      'resetAddWidgetData',
      'setWidgetFormData',
    ]),
    async getDashboards() {
      try {
        const { data } = await CustomService.getAllDashboards();
        let dashList = data.data
          .map((item) => ({
            label: item.name,
            value: item.id,
          }))
          .sort((a, b) => (a.label > b.label ? 1 : -1));
        this.dashboardList = dashList;
      } catch (error) {
        this.dashboardList = [];
      }
    },
    onDashCreateChange(isNewDash) {
      this.setWidgetFormData({
        field: 'isNewDash',
        value: isNewDash,
      });
      if (isNewDash && !this.getWidgetFormData.isDashboardValid) {
        this.setWidgetFormData({
          field: 'isDashboardValid',
          value: true,
        });
      }
    },
    handleClose() {
      this.$emit('onClose');
    },
    checkInvalidName(nameStr, strLength) {
      const regex = new RegExp(NAME_FIELD_REGEX);

      return (
        !regex.test(nameStr) || !nameStr?.trim() || nameStr?.length > strLength
      );
    },
    validateAddDashboardPage() {
      const isWidgetNameInvalid = this.checkInvalidName(
        this.getWidgetAddDashboardInfo.widgetName,
        25,
      );
      this.setWidgetFormData({
        field: 'isWidgetNameValid',
        value: !isWidgetNameInvalid,
      });

      const isDashNameInvalid = this.getWidgetFormData.isNewDash
        ? this.checkInvalidName(
            this.getWidgetAddDashboardInfo.dashboardName,
            20,
          )
        : false;
      this.setWidgetFormData({
        field: 'isDashNameValid',
        value: !isDashNameInvalid,
      });

      const isDashboardInvalid =
        !this.getWidgetFormData.isNewDash &&
        (!this.getWidgetAddDashboardInfo.dashboard ||
          !this.getWidgetAddDashboardInfo.dashboard.id);

      this.setWidgetFormData({
        field: 'isDashboardValid',
        value: !isDashboardInvalid,
      });

      return isWidgetNameInvalid || isDashNameInvalid || isDashboardInvalid;
    },
    async addDashboard() {
      try {
        const dashboardData = {
          name: this.getWidgetAddDashboardInfo.dashboardName,
          id: this.getWidgetAddDashboardInfo.dashboard?.id,
        };
        const copyDashId = this.getWidgetAddDashboardInfo.dashboard?.id;
        const { data } = !copyDashId
          ? await CustomService.addDashboard(dashboardData)
          : await CustomService.addDashboardFromId(dashboardData);

        if (data.success) {
          this.dashboardId = data.data[0].id;
          this.isNewDashCreated = true;
        } else {
          this.isNewDashCreated = false;
        }
      } catch (error) {
        notificationError(error.message || this.$t('aws.formSubmissionFailed'));
        this.isNewDashCreated = false;
        throw new Error();
      }
    },
    getWidgetReqData() {
      if (this.chartConfig.isMultiMetric) {
        return this.getMultiChartGraphConfig;
      } else {
        return this.getGraphConfig;
      }
    },
    async addWidget() {
      try {
        const widget = this.getWidgetReqData();
        const dashWidgetData = {
          id: this.getWidgetFormData.isNewDash
            ? this.dashboardId
            : this.getWidgetAddDashboardInfo.dashboard.id,
          widget,
        };
        const { data } =
          await CustomService.postDashboardTemplate(dashWidgetData);

        if (data.success) {
          this.isWidgetCreated = true;
          notificationSucess(this.$t('log.createWidgetSuccMsg'));
        } else {
          this.isWidgetCreated = false;
          notificationError(
            data.message || this.$t('aws.formSubmissionFailed'),
          );
        }
      } catch (error) {
        notificationError(error.message || this.$t('aws.formSubmissionFailed'));
        this.isWidgetCreated = false;
      }
    },
    async handleSubmit() {
      if (this.validateAddDashboardPage()) return;
      try {
        if (this.getWidgetFormData.isNewDash && !this.isNewDashCreated) {
          await this.addDashboard();
        }
        if (!this.isWidgetCreated) {
          await this.addWidget();
        }
        this.handleClose();
      } catch (error) {
        console.log('error', error);
      }
    },
    handleClear() {
      this.resetAddWidgetData('addDashboardInfo');
    },
  },
  created() {
    this.getDashboards();
    this.resetAddWidgetData('addDashboardInfo');
  },
};
</script>

<template>
  <div>
    <RexModalV3
      :show="showAddToDashboard"
      :title="$t('aws.addToDashboard')"
      :enableFooter="true"
      @onClose="handleClose"
      width="50rem"
    >
      <template #body>
        <LogWidgetAddDashboardPage />
      </template>
      <template #footer>
        <div class="footer-wrapper">
          <RexButtonV3
            variant="transparent"
            @onClick="handleClose"
            id="add-to-dashboard-cancel"
          >
            {{ $t('aws.cancel') }}
          </RexButtonV3>

          <div class="footer-submit-buttons">
            <RexButtonV3
              @onClick="handleClear"
              variant="default"
              id="add-to-dashboard-clear"
            >
              {{ $t('aws.clear') }}
            </RexButtonV3>
            <RexButtonV3
              @onClick="handleSubmit"
              variant="primary"
              id="add-to-dashboard-button"
            >
              {{ $t('aws.addToDashboard') }}
            </RexButtonV3>
          </div>
        </div>
      </template>
    </RexModalV3>
  </div>
</template>

<style lang="scss" scoped>
.add-widget-dash-radio {
  @include flex-column;
  width: 100%;
  padding: 0.625rem 0rem;
}

.existing-dashboard-wrapper {
  display: flex;
  width: 100%;
  gap: 0.313rem;
}

.add-dashboard-input-fields {
  width: 100%;
  margin: 0.625rem 0rem;
}

.footer-wrapper {
  @include flex-center-justify(space-between);
  width: 100%;
}
</style>
